import { createStyles, withStyles } from '@material-ui/core';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import CommonContainer from '../../../common/CommonContainer';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import {
  clearStandardReports,
  downloadZReport,
} from '../../../../actions/report-actions';
import {
  getHeaderTranslationForStandardReports,
  isAverageSalesReport,
  reportConfigForRegularTable,
} from './StandardReportUtils';
import RegularTable from '../../../common/table/RegularTable';
import { formatTimestampAsDate } from '../../../../utils/dates-utils';
import RegularDataGrid from '../../../common/table/RegularDataGrid';
import { isNumber } from '../../../../utils/report-utils';
import DataGridCell from '../../../common/table/DataGridCell';
import { TranslationsContext } from '../../../../config/translations';
import classNames from 'classnames';
import { NAV_BAR_WIDTH } from '../../../../const/settings';
import {
  HEADER_SIZE_DESKTOP, HEADER_SIZE_MOBILE,
  SEARCH_BUTTON_WIDTH,
} from '../../../../const/chart-const';
import PropTypes from 'prop-types';
import { REPORTS_NAMES } from './StandardReportConfig';
import StandardReportForm from './StandardReportForm';
import ChartsWrapper from '../../../common/charts/ChartsWrapper';
import { BACKGROUND_DARK, BACKGROUND_WHITE } from '../../../../const/color-const';
import { DownloadToolbar } from '../sie4/Sie4ReportConfig';
import { downloadTransactionFileForExcel } from '../../../../actions/sie4-actions';
import NoDataAvailableWithIcon from '../../../common/charts/NoDataAvailableWithIcon';
import CommonButton from '../../../common/CommonButton';
import { VerticalAlignBottomOutlined } from '@material-ui/icons';

const [CASH_REGISTER_HEIGHT_MOBILE, CASH_REGISTER_HEIGHT_DESKTOP] = [48, 60];
const [COLUMN_MEDIUM, COLUMN_LARGE, COLUMN_BREAKPOINT] = [180, 220, 8];
const [ALIGN_LEFT, ALIGN_CENTER] = ['left', 'center'];

const styles = createStyles(theme => ({
  pageWrapper: {
    width: '100%',
    background: theme.palette.type === 'light' ? BACKGROUND_WHITE : BACKGROUND_DARK,
    padding: theme.spacing(0, 0, 2, 0),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 2, 2, 2),
    },
  },
  topSectionWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      maxWidth: `calc(100vw - 300px)`,
    },
  },
  reportHeader: {
    paddingTop: theme.spacing(3),
    alignSelf: 'center',
    textAlign: 'center',
    fontSize: HEADER_SIZE_MOBILE,
    [theme.breakpoints.up('md')]: {
      fontSize: HEADER_SIZE_DESKTOP,
    },
  },
  formWrapper: {
    [theme.breakpoints.up('md')]: {
      width: `calc(100vw - ${NAV_BAR_WIDTH})`,
    },
    width: '100%',
    '& form': {
      [theme.breakpoints.down('sm')]: {
        textAlign: 'center',
      },
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
    },
    '& .MultiSelectField': {
      height: CASH_REGISTER_HEIGHT_MOBILE,
      margin: theme.spacing(1),
      [theme.breakpoints.up('md')]: {
        height: CASH_REGISTER_HEIGHT_DESKTOP,
        margin: theme.spacing(2),
      },
    },
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(1.5),
    '& button': {
      width: SEARCH_BUTTON_WIDTH,
    },
  },
  fieldsWrapper: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    '& > div.commonSelectField': {
      border: 'none',
      '& label': {
        border: 'none',
        marginLeft: 0,
      },
    },
  },
  spinner: {
    textAlign: 'center',
  },
  pagination: {
    margin: `${theme.spacing(1.5)}px 0`,
  },
  tableWrapper: {
    display: 'flex',
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(8),
    flexWrap: 'wrap',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      margin: '12px auto',
    },
  },
  buttonsWrapper: {
    marginTop: theme.spacing(2),
    '& .CommonButton-button-587': {
      maxHeight: 34,
    },
  },
}));

const DownloadToolbarWithProgress = ({
                                       reportName,
                                       formValues,
                                       machinesOptions,
                                       downloadTransactionFileForExcel,
                                     }) => {
  const [downloadProgress, setDownloadProgress] = useState(null);
  
  return (
    <DownloadToolbar
      reportName={reportName}
      formValues={formValues}
      machinesOptions={machinesOptions}
      setDownloadProgress={setDownloadProgress}
      downloadProgress={downloadProgress}
      downloadTransactionFileForExcel={downloadTransactionFileForExcel}
    />
  );
};

const CommonReport = (props) => {
  const {
    classes,
    machines,
    reports,
    fields,
    reportTitle,
    reportName,
    clearStandardReports,
    downloadTransactionFileForExcel,
  } = props;
  
  const [formValues, setFormValues] = useState(null);
  const reportHeaders = () => reports.length > 0 && Object.keys(reports[0]).filter(item => item !== 'machineID');
  const columnsWidth = () => reportHeaders().length < COLUMN_BREAKPOINT ? COLUMN_LARGE : COLUMN_MEDIUM;
  const currentAlign = reports && reportHeaders().length <= COLUMN_BREAKPOINT ? ALIGN_CENTER : ALIGN_LEFT;
  const charts = REPORTS_NAMES?.[reportName] ? REPORTS_NAMES[reportName].charts : undefined;
  const machinesOptions = machines.map(machine => ({ label: machine.Name, value: machine.Id }));
  const isAverageReport = isAverageSalesReport(reportName);
  const { formatMessage } = useIntl();
  
  React.useEffect(() => {
    clearStandardReports();
  }, [clearStandardReports, formValues]);
  
  const combinedClassNames = classNames(
    classes.pageWrapper,
    currentAlign,
  );
  
  return (
    <TranslationsContext.Consumer>
      {({ languageState }) =>
        <CommonContainer className={combinedClassNames}
                         hAlign={currentAlign}>
          <div className={classes.topSectionWrapper}>
            <h1 className={classes.reportHeader}>
              {reportTitle}
            </h1>
  
            <div className={classes.formWrapper}>
              <StandardReportForm
                fields={fields}
                reportName={reportName}
                setFormValues={setFormValues}
              />
              <ChartsWrapper charts={charts} reports={reports} languageState={languageState} reportName={reportName} />
            </div>
          </div>
  
          {reports && (reports.length > 0
              ? <div className={classes.tableWrapper}>
                {isAverageReport
                  ? <RegularTable
                    withSubheaders={true}
                    data={reports}
                    tableConfig={reportConfigForRegularTable(reportHeaders())}
                    excelButton={<CommonButton
                      label={'Excel'}
                      endIcon={<VerticalAlignBottomOutlined />}
                      onClick={() => downloadTransactionFileForExcel(reportName, formValues)}
                    />}
                  />
                  : <RegularDataGrid
                    reportName={reportName}
                    columns={
                      reportHeaders().map(header => ({
                        headerName: getHeaderTranslationForStandardReports(formatMessage, header),
                        field: header,
                        headerAlign: 'center',
                        width: columnsWidth(),
                        valueGetter: (params) => isNumber(params.value) && header !== 'Artikelnummer' ? Number(params.value) : params.value,
                        renderCell: (params) => <DataGridCell timeFormat={formatTimestampAsDate} header={header}
                                                              value={params.value} />,
                      }))}
                    rows={reports.map((report, index) => ({ ...report, id: index }))}
                    components={{
                      Toolbar: () => (
                        <DownloadToolbarWithProgress
                          reportName={reportName}
                          formValues={formValues}
                          machinesOptions={machinesOptions}
                          downloadTransactionFileForExcel={downloadTransactionFileForExcel}
                        />
                      ),
                    }}
                  />}
              </div>
              : <NoDataAvailableWithIcon />
          )}
        
        </CommonContainer>
      }
    </TranslationsContext.Consumer>
  );
};

CommonReport.propTypes = {
  classes: PropTypes.object,
  machines: PropTypes.array,
  reports: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  fields: PropTypes.array,
  reportTitle: PropTypes.object,
  reportName: PropTypes.string,
  clearStandardReports: PropTypes.func,
  downloadTransactionFileForExcel: PropTypes.func,
};

const decorate = compose(
  withStyles(styles),
  connect(
    store => ({
      machines: store.user.machines,
      reports: store.report.standard,
    }),
    dispatch => bindActionCreators({
      downloadTransactionFileForExcel,
      downloadZReport,
      clearStandardReports,
    }, dispatch)),
);

export default decorate(CommonReport);

